import * as React from 'react'
import { cn } from '../../lib/cn'
import { StageEmptyState } from './StageEmptyState'
import type { PipelineStageData, StageKind } from './types'

const STAGE_KIND_ACCENT: Record<StageKind, string> = {
  preauth: 'var(--pe-accent-preauth)',
  postauth: 'var(--pe-accent-postauth)',
  postissue: 'var(--pe-accent-postissue)',
  custom: 'var(--pe-text-tertiary)',
}

/** Min height of an empty stage card so the centered prompt has room to
 *  breathe without the card stretching to the full canvas height. */
const EMPTY_STAGE_MIN_H = 248

const DEFAULT_EMPTY_HINT = {
  title: 'Nothing here yet',
  description: 'Add an action to run at this stage.',
}

export interface StageColumnProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Stage descriptor. Body content rides on `stage.children`. */
  stage: PipelineStageData
}

/**
 * Single stage column — width fixed at 320px to match the mockup
 * `--col-w`. Renders the accent border, label + counter + purpose
 * header, body (children) and optional footer. Caller supplies the
 * actual blocks (SystemChip, ContainerBlock, custom Block, etc.).
 *
 * Source of truth: `mockups/unified-pipeline.html` `.stage`.
 */
export const StageColumn = React.forwardRef<HTMLDivElement, StageColumnProps>(function StageColumn(
  { className, stage, style, ...rest },
  ref,
) {
  const accent = stage.accentColor ?? STAGE_KIND_ACCENT[stage.kind ?? 'custom']
  const isEmpty = React.Children.count(stage.children) === 0

  const composedStyle: React.CSSProperties = {
    width: 320,
    minWidth: 320,
    ['--stage-accent' as string]: accent,
    background: 'var(--pe-bg-card)',
    borderColor: 'var(--pe-border-card)',
    // Empty stages stay compact + vertically centered instead of
    // stretching the full canvas height with a lonely Add button.
    // alignSelf overrides the slot wrapper's items-stretch.
    ...(isEmpty ? { alignSelf: 'center', minHeight: EMPTY_STAGE_MIN_H } : null),
    ...style,
  }

  return (
    <div
      ref={ref}
      className={cn(
        'relative flex flex-col overflow-hidden rounded-[10px] border transition-colors',
        'hover:[border-color:var(--pe-border-strong)]',
        className,
      )}
      style={composedStyle}
      data-testid="pipeline-stage-column"
      data-stage-id={stage.id}
      data-stage-kind={stage.kind ?? 'custom'}
      {...rest}
    >
      <span
        aria-hidden
        className="absolute left-0 right-0 top-0"
        style={{ height: 2, background: 'var(--stage-accent)', opacity: 0.6 }}
      />

      <div
        className="px-4 pt-3.5 pb-3 border-b shrink-0"
        style={{
          borderColor: 'var(--pe-border-subtle)',
          backgroundImage: 'linear-gradient(180deg, var(--pe-bg-card-elev) 0%, transparent 100%)',
        }}
      >
        <div className="flex items-center gap-2.5 mb-1">
          <span
            className="rounded border px-1.5 py-0.5 font-mono text-[10px]"
            style={{
              letterSpacing: '0.10em',
              color: 'var(--pe-text-muted)',
              background: 'color-mix(in srgb, var(--pe-text-primary) 3%, transparent)',
              borderColor: 'var(--pe-border-subtle)',
            }}
          >
            {stage.num}
          </span>
          <span
            className="font-mono text-[12px] font-semibold uppercase"
            style={{ letterSpacing: '0.12em', color: 'var(--stage-accent)' }}
          >
            {stage.label}
          </span>
          {stage.counter !== undefined && (
            <span
              className="ml-auto rounded-full border px-2 py-0.5 font-mono text-[10px]"
              style={{
                color: 'var(--pe-text-muted)',
                background: 'color-mix(in srgb, var(--pe-text-primary) 3%, transparent)',
                borderColor: 'var(--pe-border-subtle)',
              }}
            >
              {stage.counter}
            </span>
          )}
        </div>
        {stage.purpose && (
          <p className="text-[11px] leading-[1.4]" style={{ color: 'var(--pe-text-tertiary)' }}>
            {stage.purpose}
          </p>
        )}
      </div>

      {isEmpty ? (
        <div
          className="flex-1 flex flex-col items-center justify-center p-4"
          data-testid="pipeline-stage-body"
        >
          <StageEmptyState
            hint={stage.emptyHint ?? DEFAULT_EMPTY_HINT}
            accent={accent}
            action={stage.footer}
          />
        </div>
      ) : (
        <>
          <div
            className="flex-1 overflow-y-auto p-3 flex flex-col gap-1.5"
            data-testid="pipeline-stage-body"
          >
            {stage.children}
          </div>

          {stage.footer && (
            <div
              className="px-3 py-2.5 border-t shrink-0"
              style={{ borderColor: 'var(--pe-border-subtle)' }}
            >
              {stage.footer}
            </div>
          )}
        </>
      )}
    </div>
  )
})
